import { PayoutSetupContext } from '#app/components/PayoutSetup/PayoutSetupContext.js';
import {
  type ContraBalance,
  ContraPaymentsContext,
  type ContraPaymentsContextType,
} from '#app/contexts/ContraPayments/ContraPaymentsContext.js';
import { PersonaContext } from '#app/contexts/PersonaContext/PersonaContext.js';
import {
  AllowedBillingAccountTypes,
  StripeContext,
  type StripeContextType,
} from '#app/contexts/StripeContext/StripeContext.js';
import { AddBillingMethodWorkflowReactContext } from '#app/features/wallet/components/AddAccountWorkFlows/AddBillingMethodWorkFlow/contexts/AddBillingMethodMachineContext.js';
import { type AddBillingMethodWorkflowVariant } from '#app/features/wallet/components/AddAccountWorkFlows/types.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCallback, useContext, useMemo } from 'react';

type UseContraPaymentsProps = {
  allowedPaymentMethods: Array<AllowedBillingAccountTypes['__typename']>;
};

type UseContraPaymentsReturnType = {
  balance: ContraBalance;

  billingAccounts: StripeContextType['billingAccounts'];

  defaultBillingCard: StripeContextType['defaultBillingCard'];

  initiateAddBillingMethodWorkflow: (
    workflowVariant?: AddBillingMethodWorkflowVariant,
  ) => void;

  /**
   * Helper function that will start the addPaymentMethod flow, which also includes
   * the onboarding flows for stripe or persona (eventually).
   */
  initiateSetupPayments: () => void;

  newPaymentMethodAdded?: string;

  /**
   * Payout methods that have been created for the user.
   */
  payoutMethods: ContraPaymentsContextType['payoutMethods'];

  setNewPaymentMethodAdded: (value?: string) => void;

  /**
   * Has the user requested and had ACH payments approved
   * @type {boolean}
   */
  userHasApprovedAchPayments: boolean;

  /**
   * Does the user currently have any billing accounts attached to their account
   * @type {boolean}
   */
  userHasBillingAccounts: boolean;

  /**
   * Does the user currently have payments setup - meaning they have at least 1 valid payout account and there
   * KYC requirements for stripe or persona are met.
   * @type {boolean}
   */
  userHasPaymentsSetup: boolean;

  /**
   * Does the user currently have any valid payout accounts attached to their account.
   * @type {boolean}
   */
  userHasPayoutAccounts: boolean;
};

/**
 * Helper methods and properties related to all things on Contra that are
 * payments related. Including Stripe, Persona, and Crypto Currency
 * related payment methods and properties.
 * @returns {UseContraPaymentsReturnType}
 * @example
 * ```tsx
 * function Component() {
 *   const {initiateSetupPayments} = useContraPayments();
 * }
 * ```
 */
export const useContraPayments = (
  props?: UseContraPaymentsProps,
): UseContraPaymentsReturnType => {
  const paymentsContext = useContext(ContraPaymentsContext);
  const addBillingContext = useContext(AddBillingMethodWorkflowReactContext);
  const personaContext = useContext(PersonaContext);
  const stripeContext = useContext(StripeContext);
  const payoutSetupContext = useContext(PayoutSetupContext);

  const { balance, payoutMethods } = paymentsContext;
  const {
    initiateAddBillingMethodWorkflow,
    newPaymentMethodAdded,
    setNewPaymentMethodAdded,
  } = addBillingContext;
  const { hasIdentityVerifiedThroughPersona } = personaContext;

  const hasPaypalAccount = payoutMethods.some(
    (payoutMethod) => payoutMethod.type === 'PAYPAL',
  );
  const hasCryptoPayoutAccounts = payoutMethods.some(
    (payoutMethod) => payoutMethod.type === 'CRYPTO_ADDRESS',
  );
  const hasPayoneerAccount = payoutMethods.some(
    (payoutMethod) => payoutMethod.type === 'PAYONEER',
  );
  const hasAirwallexBeneficiary = payoutMethods.some(
    (payoutMethod) => payoutMethod.type === 'AIRWALLEX_BENEFICIARY',
  );
  const hasStripePayoutAccounts = payoutMethods.some((payoutMethod) =>
    ['BANK_ACCOUNT', 'CARD'].includes(payoutMethod.type),
  );

  const {
    accountIsReady,
    achApproved,
    billingAccounts,
    defaultBillingCard,
    hasBillingAccounts: hasStripeBillingAccounts,
  } = stripeContext;

  let billingAccountsToShow = billingAccounts;
  if (props?.allowedPaymentMethods && props.allowedPaymentMethods.length > 0) {
    billingAccountsToShow = billingAccounts.filter((account) =>
      props.allowedPaymentMethods.includes(account.__typename),
    );
  }

  const { track } = useAnalytics();

  const initiateSetupPayments = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'coin_payout_add_an_account',
    });

    payoutSetupContext.start();
  }, [payoutSetupContext, track]);

  // true when identity + at least 1 external account exists
  const stripeAccountIsReady = accountIsReady && hasStripePayoutAccounts;

  // has persona id + crypto account
  const cryptoAccountReady =
    hasIdentityVerifiedThroughPersona && hasCryptoPayoutAccounts;

  // user has onboarded with stripe + has external accounts or...
  // user has onboarded with persona + has crypto accounts
  // or has connected PayPal account
  const userHasPaymentsSetup =
    stripeAccountIsReady ||
    cryptoAccountReady ||
    hasPaypalAccount ||
    hasAirwallexBeneficiary ||
    hasPayoneerAccount;

  // does the user have ANY payout accounts created
  const userHasPayoutAccounts = payoutMethods.length > 0;

  // does the user have ANY payment accounts
  const userHasBillingAccounts = hasStripeBillingAccounts;

  // user requested and had ACH payments approved
  const userHasApprovedAchPayments = achApproved;

  const hookResponse = useMemo(() => {
    return {
      balance,
      billingAccounts: billingAccountsToShow,
      defaultBillingCard,
      initiateAddBillingMethodWorkflow,
      initiateSetupPayments,
      newPaymentMethodAdded,
      payoutMethods,
      setNewPaymentMethodAdded,
      userHasApprovedAchPayments,
      userHasBillingAccounts,
      userHasPaymentsSetup,
      userHasPayoutAccounts,
    };
  }, [
    balance,
    billingAccountsToShow,
    defaultBillingCard,
    initiateAddBillingMethodWorkflow,
    initiateSetupPayments,
    newPaymentMethodAdded,
    payoutMethods,
    setNewPaymentMethodAdded,
    userHasApprovedAchPayments,
    userHasBillingAccounts,
    userHasPaymentsSetup,
    userHasPayoutAccounts,
  ]);

  return hookResponse;
};
